/* iter55f80 — Export Jobs section.
 *
 * Compact list of the writer's queued/completed background PDF export
 * jobs. Auto-refreshes every 4 seconds while a job is `queued` or
 * `running`, downloads on-demand once a job is `done`. Living inside
 * the Export dialog keeps the UI close to where the writer kicks the
 * jobs off, without needing a separate route.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, X, CheckCircle2, AlertCircle, Trash2, Mail } from 'lucide-react';
import { exportApi } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function shortAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ExportJobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await exportApi.listPdfJobs(20);
      setJobs(r.jobs || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while any job is queued or running.
  useEffect(() => {
    const running = jobs.some((j) => j.status === 'queued' || j.status === 'running');
    if (!running) return undefined;
    const iv = setInterval(refresh, 4000);
    return () => clearInterval(iv);
  }, [jobs, refresh]);

  if (!jobs.length && !loading) return null;

  return (
    <div className="mt-4 rounded-md border border-border bg-card" data-testid="export-jobs-panel">
      <div className="flex items-center justify-between border-b border-border/70 px-3 py-2">
        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
          Recent background exports
        </div>
        <button
          type="button"
          onClick={refresh}
          className="text-[10.5px] uppercase tracking-wider text-primary hover:underline"
          data-testid="export-jobs-refresh"
        >
          Refresh
        </button>
      </div>
      <ul className="divide-y divide-border/70">
        {jobs.map((j) => (
          <li key={j.id} className="flex items-center gap-2 px-3 py-2" data-testid={`export-job-row-${j.id}`}>
            <StatusIcon status={j.status} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-medium">{j.filename || j.kind}</div>
              <div className="flex items-center gap-2 text-[10.5px] text-muted-foreground">
                <span className="uppercase tracking-wider">{j.kind}</span>
                <span>·</span>
                <span>{j.page_size?.replace(/_/g, ' ')}</span>
                <span>·</span>
                <span>{shortAgo(j.created_at)}</span>
                {j.bytes_len ? (<><span>·</span><span>{(j.bytes_len / 1024).toFixed(0)} KB</span></>) : null}
                {j.emailed && (<span title="Completion email sent" className="ml-1 inline-flex items-center gap-1 text-emerald-700"><Mail className="h-3 w-3" /></span>)}
              </div>
              {j.status === 'error' && j.error && (
                <div className="mt-1 text-[10.5px] text-red-700">{j.error}</div>
              )}
            </div>
            {j.status === 'done' && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await exportApi.downloadPdfJob(j.id, j.filename);
                    toast.success('Downloading…');
                  } catch { toast.error('Download failed.'); }
                }}
                className="flex h-7 items-center gap-1 rounded-sm border border-primary/40 bg-primary/5 px-2 text-[11px] font-medium text-primary hover:bg-primary/10"
                data-testid={`export-job-download-${j.id}`}
              >
                <Download className="h-3 w-3" /> Download
              </button>
            )}
            <button
              type="button"
              title="Delete export"
              onClick={async () => {
                try {
                  await exportApi.deletePdfJob(j.id);
                  setJobs((prev) => prev.filter((x) => x.id !== j.id));
                } catch { toast.error('Delete failed.'); }
              }}
              className="flex h-7 w-7 items-center justify-center rounded-sm border border-border text-muted-foreground hover:border-red-400 hover:text-red-600"
              data-testid={`export-job-delete-${j.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === 'done') {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" data-testid="export-job-status-done" />;
  }
  if (status === 'error') {
    return <AlertCircle className="h-4 w-4 shrink-0 text-red-600" data-testid="export-job-status-error" />;
  }
  if (status === 'running') {
    return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" data-testid="export-job-status-running" />;
  }
  return <Loader2 className={cn('h-4 w-4 shrink-0 text-muted-foreground')} data-testid="export-job-status-queued" />;
}
